import React from "react";
import { Button } from "./ui/button";
import { isLazyChunkFailure } from "../lib/lazyWithRetry";

/**
 * Error Boundary specifically for handling chunk loading failures
 * Catches errors from lazy-loaded components and provides recovery options
 */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const isChunkError = ChunkErrorBoundary.isChunkLoadFailure(error);

    this.setState({
      error,
      errorInfo,
    });

    // Log to console for debugging
    console.error("ChunkErrorBoundary caught an error:", error, errorInfo);

    // If it's a chunk error and auto-reload is enabled, reload after a delay
    if (isChunkError && this.props.autoReload) {
      setTimeout(() => {
        ChunkErrorBoundary.hardReload();
      }, 2000);
    }
  }

  // Recognises a chunk-load / dynamic-import failure across browsers.
  // Each browser phrases the error differently; missing one of these means
  // the boundary falls through to the generic "Something went wrong" path
  // instead of the friendly "New version available — please reload" modal.
  // Last debugged after the 2026-05-10 incident where Levi's post-deploy
  // refresh hit a stale-chunk mismatch and saw the cryptic generic UI.
  static isChunkLoadFailure(error) {
    if (!error) return false;
    if (error.name === "ChunkLoadError") return true;
    const msg = (error.message || "").toString();
    // Patterns observed in the wild:
    //   Chrome:  "Failed to fetch dynamically imported module: <url>"
    //   Firefox: "error loading dynamically imported module: <url>"
    //   Safari:  "Importing a module script failed."
    //   Webpack: "Loading chunk N failed."
    //   Webpack: "Loading CSS chunk N failed."
    //   Generic: "Unexpected token '<'"  (chunk fetch returned index.html
    //            instead of JS — happens when the chunk no longer exists
    //            and the static server falls back to SPA index.html)
    //   Stale-bundle TDZ: "Cannot access 'ke' before initialization" — when
    //            a tab is open across a deploy and a chunk 404s back to
    //            index.html, the half-loaded webpack runtime can hit a TDZ
    //            on a minified binding (1-3 char identifier).
    //
    //            THIS ONE IS GATED ON A MARKER, and that gate is the whole
    //            point of the pattern's shape. Every identifier in the
    //            production bundle is 1-3 characters, so the bare regex below
    //            also matched a Temporal Dead Zone thrown during a normal
    //            RENDER by current, healthy code — which is a bug, not a stale
    //            chunk. That is exactly what happened to the Employee page on
    //            2026-10-07: `openId` was read in a useMemo dependency array
    //            45 lines above its own useState, every render threw, and the
    //            user was shown "New Easter Eggs Have Been Added — Please
    //            Refresh", whose only button re-ran the same crash forever.
    //            A render-time TDZ never touches the import promise, so
    //            lazyWithRetry never marks it. Only a rejection that came out
    //            of a chunk import carries the marker, so gating on it keeps
    //            the stale-chunk recovery AND stops the boundary from
    //            disguising real crashes as update notices.
    return (
      msg.includes("Loading chunk") ||
      msg.includes("Loading CSS chunk") ||
      msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("error loading dynamically imported module") ||
      msg.includes("Importing a module script failed") ||
      msg.includes("Unexpected token '<'") ||
      (isLazyChunkFailure(error) &&
        /^Cannot access '\w{1,3}' before initialization$/.test(msg))
    );
  }

  // Force a full navigation to a clean URL so lazyWithRetry gets a fresh
  // chance to auto-reload on the next chunk failure.
  //
  // Strips _r and clears _chunkReloadReturn so lazyWithRetry sees
  // alreadyReloaded=false and can set its own fresh _r if chunks still
  // fail — without this, hardReload was setting a new _r itself, which
  // immediately put lazyWithRetry into alreadyReloaded=true and sent
  // the user straight back to this boundary in an infinite loop.
  static hardReload() {
    const url = new URL(window.location.href);
    url.searchParams.delete("_r");
    sessionStorage.removeItem("_chunkReloadReturn");
    window.location.replace(url.toString());
  }

  handleReload = () => {
    ChunkErrorBoundary.hardReload();
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Check if it's a chunk loading error
      const isChunkError = ChunkErrorBoundary.isChunkLoadFailure(this.state.error);

      if (isChunkError) {
        // On-theme "new version available" modal. Glassmorphism panel,
        // dark surface, blue accent, sparkle icon. Headline on top,
        // kaomoji refresh prompt as the bottom line per Levi's spec.
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="glass-panel max-w-md w-full rounded-2xl p-8 text-center">
              <div className="mb-5">
                <svg
                  className="w-14 h-14 mx-auto text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                  />
                </svg>
              </div>

              <h2 className="text-xl font-semibold text-white mb-3">
                New Easter Eggs Have Been Added
              </h2>

              <p className="font-mono text-base text-blue-300 mb-6 select-none">
                {"(>$_$)> Please Refresh <($_$<)"}
              </p>

              <Button
                onClick={this.handleReload}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg shadow-md shadow-blue-500/30"
              >
                Reload Page
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="flex items-center justify-center min-h-[400px] p-8">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="mb-4">
              <svg
                className="w-16 h-16 text-red-500 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            <h2 className="text-xl font-bold text-gray-800 mb-2">
              Something went wrong
            </h2>

            <p className="text-gray-600 mb-6">
              An error occurred while loading this content. Please try again.
            </p>

            <div className="flex gap-3 justify-center">
              <Button
                onClick={this.handleReload}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2"
              >
                Reload Page
              </Button>

              <Button
                onClick={this.handleReset}
                variant="outline"
                className="px-6 py-2"
              >
                Try Again
              </Button>
            </div>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Error Details (Development Only)
                </summary>
                <pre className="mt-2 text-xs bg-gray-100 p-3 rounded overflow-auto max-h-40">
                  {this.state.error.toString()}
                  {"\n"}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
